import { Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';

interface ProfileCompletenessBadgeProps {
    rate?: number | null;
    completedSections?: string[];
}

const SECTION_LABELS: Record<string, string> = {
    profile_info: 'Informations profil',
    profile_photo: 'Photo de profil',
    banner_image: 'Image de couverture',
    biography: 'Biographie',
    salary: 'Salaire',
    contact: 'Contact',
    professional_skills: 'Competences professionnelles',
    interpersonal_skill: 'Competences interpersonnelles',
    experience: 'Experience',
    education: 'Education',
    certificates_training: 'Certificats et formations',
};

const SECTION_KEYS = Object.keys(SECTION_LABELS);

const ProfileCompletenessBadge: React.FC<ProfileCompletenessBadgeProps> = ({
    rate,
    completedSections,
}) => {
    const theme = useTheme();

    const normalizedRate = Math.max(0, Math.min(100, Math.round(rate ?? 0)));

    const missingSectionsLabel = useMemo(() => {
        const completed = new Set(completedSections || []);
        const missing = SECTION_KEYS
            .filter((key) => !completed.has(key))
            .map((key) => SECTION_LABELS[key]);

        if (missing.length === 0) {
            return 'Aucune section manquante';
        }

        return `Sections manquantes: ${missing.join(', ')}`;
    }, [completedSections]);

    if (normalizedRate >= 100) {
        return (
            <Tooltip title={missingSectionsLabel}>
                <Chip
                    label="Profil complet"
                    size="small"
                    sx={{
                        backgroundColor: theme.palette.successGreen?.main ?? '#4caf50',
                        color: theme.palette.neutralWhite?.main ?? '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                    }}
                />
            </Tooltip>
        );
    }

    if (normalizedRate <= 0) {
        return (
            <Tooltip title={missingSectionsLabel}>
                <Chip
                    label="Profil vide"
                    size="small"
                    sx={{
                        backgroundColor: theme.palette.errorRed?.main ?? '#f44336',
                        color: theme.palette.neutralWhite?.main ?? '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                    }}
                />
            </Tooltip>
        );
    }

    return (
        <Tooltip title={missingSectionsLabel}>
            <Chip
                label={`Profil ${normalizedRate}%`}
                size="small"
                sx={{
                    backgroundColor: theme.palette.warningOrange?.main ?? '#ff9800',
                    color: theme.palette.neutralWhite?.main ?? '#fff',
                    fontWeight: 600,
                    fontSize: 11,
                }}
            />
        </Tooltip>
    );
};

export default ProfileCompletenessBadge;
